import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, ChevronRight } from 'lucide-react';
import { HostBottomNav } from '../../components/HostBottomNav';
import NotificationBellButton from '../../components/NotificationBellButton';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { useProfilePhotoUpload } from '../../hooks/useProfilePhotoUpload';
import { resolveAvatarUrl } from '../../utils/resolveAvatarUrl';
import WelcomeTourSheet from '../../components/WelcomeTourSheet';
import PageInfoButton from '../../components/PageInfoButton';
import { hostWelcomeTourSlides } from '../../components/welcomeTourSlides';
import ThemeToggle from '../../components/ThemeToggle';
import ProfileThemeFlash from '../../components/ProfileThemeFlash';
import ProfilePalettePicker from '../../components/ProfilePalettePicker';
import LogoutConfirmModal from '../../components/LogoutConfirmModal';

export default function HostProfilePage({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { fileInputRef, uploading, openPicker, handleFileChange, user } = useProfilePhotoUpload();
  const displayName = user?.name || user?.username || 'Host';
  const [tourOpen, setTourOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const requestLogout = () => setConfirmLogout(true);
  const handleLogout = () => {
    setConfirmLogout(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    navigate('/login', { replace: true });
  };

  const menuItems = [
    { label: 'Account Settings', action: () => navigate('/host/settings') },
    { label: 'Invite Hosts', action: () => navigate('/host/referrals') },
    { label: 'My Listings', action: () => navigate('/host/listings') },
    { label: 'Allure Support', action: () => navigate('/support') },
    { label: 'Switch to..', action: () => navigate('/switch-role', { state: { fromPath: location.pathname } }) },
  ];

  return (
    <div className="app-shell" data-testid="host-profile-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <ProfileThemeFlash />
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageInfoButton
          onClick={() => setTourOpen(true)}
          testId="host-profile-info-button"
          ariaLabel="Replay Allure tour"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ThemeToggle />
          <NotificationBellButton buttonTestId="host-profile-bell-button" popupTestId="host-profile-notification-popup" />
        </div>
      </div>
      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 132, height: 118, objectFit: 'contain' }} />

        {/* Profile card - gradient tint to distinguish from menu */}
        <div className="profile-name-card" style={{ ...hostCardStyle, width: '100%', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, marginTop: 8, background: 'var(--profile-card-gradient)', border: 'none', boxShadow: 'none' }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div
              onClick={openPicker}
              data-testid="host-profile-photo-target"
              style={{
                width: 72, height: 72, borderRadius: '50%', background: 'var(--surface-2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid var(--surface)',
                overflow: 'hidden', cursor: uploading ? 'wait' : 'pointer',
                opacity: uploading ? 0.7 : 1, transition: 'opacity 200ms ease',
              }}
            >
              {resolveAvatarUrl(user?.photo_url) ? (
                <img src={resolveAvatarUrl(user.photo_url)} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 26, fontWeight: 700, color: 'var(--text-2)' }}>{displayName.charAt(0)}</span>
              )}
            </div>
            <button
              onClick={openPicker}
              disabled={uploading}
              aria-label="Change profile photo"
              data-testid="host-profile-edit-button"
              style={{ position: 'absolute', bottom: -2, left: -2, width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', border: '1px solid var(--accent-purple)', padding: 0, cursor: uploading ? 'wait' : 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
            >
              <Camera size={13} color="var(--accent-purple)" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              data-testid="host-profile-photo-input"
            />
          </div>
          <div>
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{displayName}</p>
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', margin: 0 }}>Host</p>
          </div>
        </div>

        {/* Palette preview strip — tap a swatch to retint the whole app live. */}
        <ProfilePalettePicker testIdPrefix="host-profile-palette" />

        {/* Menu */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '100%' }}>
          {/* Featured: Add an Experience — pinned above the rest and styled as a colored accent CTA */}
          <button
            onClick={() => navigate('/host/experience/new')}
            data-testid="host-profile-add-an-experience"
            style={{
              ...hostCardStyle,
              width: '82%',
              alignSelf: 'center',
              background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-purple-soft, #a78bfa) 100%)',
              border: 'none',
              cursor: 'pointer',
              padding: '11px 16px',
              marginBottom: 2,
              fontFamily: "'Inria Serif', serif",
              fontSize: 15,
              fontWeight: 700,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              textAlign: 'left',
              boxShadow: '0 8px 20px rgba(112, 63, 246, 0.28)',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span aria-hidden="true" style={{ fontSize: 16 }}>✦</span>
              Refer an Experience
            </span>
            <ChevronRight size={16} color="#FFFFFF" />
          </button>
          {menuItems.map((item) => (
            <button key={item.label} onClick={item.action} data-testid={`host-profile-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} style={{ ...hostCardStyle, width: '100%', background: 'var(--surface)', border: '1px solid var(--card-border)', cursor: 'pointer', padding: '14px 16px', fontFamily: "'Inria Serif', serif", fontSize: 17, color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left' }}><span>{item.label}</span><ChevronRight size={18} color="var(--text-2)" /></button>
          ))}
          <button onClick={requestLogout} data-testid="host-profile-logout-button" style={{ ...hostCardStyle, width: '100%', background: 'var(--surface)', border: '1px solid var(--accent-purple)', cursor: 'pointer', padding: '14px 16px', marginTop: 2, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--accent-purple)' }}>Log Out</button>
        </div>
      </div>
      <HostBottomNav />

      <WelcomeTourSheet
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        slides={hostWelcomeTourSlides}
        testIdPrefix="host-profile-welcome-tour"
      />

      <LogoutConfirmModal
        open={confirmLogout}
        onCancel={() => setConfirmLogout(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}
